import { DirectUpload } from "@rails/activestorage"
import { Loader2, Paperclip, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { MissionAttachment } from "@/types/missions"

/**
 * MissionAttachmentsDropzone — Active Storage direct-upload dropzone for
 * the Mission brief (UX Spec §2 § Attachments).
 *
 * **Wire shape (OKS-610 — two decoupled signals):**
 *   - NEW uploads: one `<input type="hidden" name="mission[files][]"
 *     value="<signed_id>">` per newly-uploaded blob. The controller
 *     APPENDS these via `mission.files.attach` (kept existing files are
 *     NOT re-submitted — the server preserves them).
 *   - REMOVED existing files: one `<input type="hidden"
 *     name="mission[remove_file_signed_ids][]" value="<signed_id>">` per
 *     existing file the user X'd. The controller scans the mission's own
 *     attachments, matches `blob.signed_id`, and purges the match
 *     (brand-scoped, idempotent). Removal is pending until Save.
 *
 * **Caps (PRD §3, server enforces too)**:
 *   - ≤10 files
 *   - ≤10 MB per file
 *   - MIME `image/* | video/* | application/pdf`
 *
 * Client-side rejection is UX (immediate per-file alert), the server is
 * the floor — `Mission#files_within_caps` validates on save.
 *
 * **Direct-upload endpoint**: `/rails/active_storage/direct_uploads` (Rails
 * default — no controller code on our side). DirectUpload streams the
 * blob to storage, then returns the `signed_id` we ride into the form.
 *
 * **A11y**:
 *   - The dropzone is a real `<button>` so Tab + Enter triggers the file
 *     picker (the visible drag-and-drop is an enhancement, not the only
 *     interaction).
 *   - Per-file remove buttons carry `aria-label="<filename> — remove"`.
 *   - Upload progress fires a `role="status"` SR announcement.
 *   - Removing a row restores focus (OKS-623, UX Spec §6): next removable
 *     row's X → previous removable row's X → the browse control. Error rows
 *     have no X and are skipped as focus targets. Focus is moved after the
 *     removal commits (a `useEffect` keyed on `files`), so the target node
 *     exists — no async timing.
 *   - The remove (X) control's visible glyph stays small (`size-3.5`) while
 *     an absolutely-positioned `::before` overlay expands the interactive
 *     hit area to ≥44×44px (WCAG 2.5.5) without changing row height.
 */
const DIRECT_UPLOAD_URL = "/rails/active_storage/direct_uploads"
const ACCEPTED_MIME = "image/*,video/*,application/pdf"
const MAX_BYTES = 10 * 1024 * 1024 // 10 MB
const MAX_COUNT = 10

interface FileEntry {
  /** Stable client id — used as React key. */
  uid: string
  filename: string
  byte_size: number
  content_type: string
  /** "uploading" until DirectUpload resolves; "ready" when signed_id is set. */
  status: "uploading" | "ready" | "error"
  signed_id: string | null
  /**
   * True for files already attached to the mission (from `defaultValue`);
   * false for uploads added this session. OKS-610: drives which signal a
   * removal emits — a removed PERSISTED file posts its signed_id in
   * `mission[remove_file_signed_ids][]`; a removed NEW file just drops
   * client-side (it was never attached).
   */
  persisted: boolean
  /** 0–100, for the per-file progress bar. */
  progress: number
  /** Per-file error message — only set when status === "error". */
  error: string | null
}

interface MissionAttachmentsDropzoneProps {
  /** Existing attachments echoed by the Edit page. */
  defaultValue?: MissionAttachment[]
  /** Wire field name for NEW uploads. Defaults to `mission[files][]`. */
  name?: string
  /**
   * Wire field name for removed EXISTING files (OKS-610). Defaults to
   * `mission[remove_file_signed_ids][]` — read directly by
   * `MissionsController#apply_media_removals`.
   */
  removeName?: string
  /** Test ID for the wrapper. */
  testId?: string
  /**
   * OKS-928 — server-side `errors.files` from the Inertia error bag, one
   * entry per rejected file. Rendered one line each beneath the dropzone.
   * Before this ticket the component took no error prop at all, so these
   * messages were produced by the model and displayed to nobody.
   */
  errors?: string[]
}

export function MissionAttachmentsDropzone({
  defaultValue = [],
  name = "mission[files][]",
  removeName = "mission[remove_file_signed_ids][]",
  testId = "mission-attachments-dropzone",
  errors,
}: MissionAttachmentsDropzoneProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  // OKS-623 — where focus should land after the next removal commits.
  // `handleRemove` computes this BEFORE filtering (adjacency is known from
  // the current list); a `useEffect` keyed on `files` consumes it once the
  // new DOM is committed. `null` when no removal is pending.
  const pendingFocusRef = useRef<
    { type: "row"; uid: string } | { type: "browse" } | null
  >(null)
  const [files, setFiles] = useState<FileEntry[]>(() =>
    defaultValue.map((a) => ({
      uid: `existing-${a.signed_id}`,
      filename: a.filename,
      byte_size: a.byte_size,
      content_type: a.content_type,
      status: "ready",
      signed_id: a.signed_id,
      progress: 100,
      error: null,
      persisted: true,
    })),
  )
  // OKS-610 — signed_ids of EXISTING files the user removed this session.
  // Emitted as `mission[remove_file_signed_ids][]` so the server purges
  // them on submit (removal is pending until Save).
  const [removedSignedIds, setRemovedSignedIds] = useState<string[]>([])
  const [isDragOver, setIsDragOver] = useState(false)

  const acceptFiles = useCallback(
    (incoming: FileList | File[]) => {
      const list = Array.from(incoming)
      const free = MAX_COUNT - files.length
      const accepted = list.slice(0, free)

      const validated = accepted.map<FileEntry>((file) => {
        const uid = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        if (file.size > MAX_BYTES) {
          return {
            uid,
            filename: file.name,
            byte_size: file.size,
            content_type: file.type,
            status: "error",
            signed_id: null,
            progress: 0,
            error: t("missions.form.attachments.too_large", {
              filename: file.name,
            }),
            persisted: false,
          }
        }
        if (
          !file.type.startsWith("image/") &&
          !file.type.startsWith("video/") &&
          file.type !== "application/pdf"
        ) {
          return {
            uid,
            filename: file.name,
            byte_size: file.size,
            content_type: file.type,
            status: "error",
            signed_id: null,
            progress: 0,
            error: t("missions.form.attachments.invalid_type", {
              filename: file.name,
            }),
            persisted: false,
          }
        }
        return {
          uid,
          filename: file.name,
          byte_size: file.size,
          content_type: file.type,
          status: "uploading",
          signed_id: null,
          progress: 0,
          error: null,
          persisted: false,
        }
      })

      const tooMany = list.length > free

      setFiles((prev) => [...prev, ...validated])

      // Kick off DirectUpload for the validated-but-uploading entries.
      validated.forEach((entry, idx) => {
        if (entry.status !== "uploading") return
        const file = accepted[idx]
        kickOffUpload(file, entry.uid, setFiles)
      })

      if (tooMany) {
        // Append a synthetic error entry so the user sees the cap message
        // — no actual blob to track.
        setFiles((prev) => [
          ...prev,
          {
            uid: `cap-${Date.now()}`,
            filename: t("missions.form.attachments.too_many"),
            byte_size: 0,
            content_type: "",
            status: "error",
            signed_id: null,
            progress: 0,
            error: t("missions.form.attachments.too_many"),
            persisted: false,
          },
        ])
      }
    },
    [files.length, t],
  )

  const handleFileInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) acceptFiles(event.target.files)
      // Reset so re-picking the same file fires onChange again.
      event.target.value = ""
    },
    [acceptFiles],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setIsDragOver(false)
      if (event.dataTransfer.files) acceptFiles(event.dataTransfer.files)
    },
    [acceptFiles],
  )

  const handleRemove = useCallback(
    (uid: string) => {
      // OKS-623 — compute the post-removal focus target from the CURRENT
      // list, BEFORE the filter, so adjacency is known. Only removable rows
      // (status !== "error") carry an X, so error rows are transparent to
      // the target computation. Fallback order (UX Spec §6): next removable
      // row's X → previous removable row's X → browse control.
      const removable = files.filter((f) => f.status !== "error")
      const removedIdx = removable.findIndex((f) => f.uid === uid)
      const nextUid = removable[removedIdx + 1]?.uid
      const prevUid = removable[removedIdx - 1]?.uid
      const targetUid = nextUid ?? prevUid
      pendingFocusRef.current = targetUid
        ? { type: "row", uid: targetUid }
        : { type: "browse" }

      // OKS-610 — a removed PERSISTED file must signal the server to purge
      // it (removal-by-omission is not enough: AS `files=` semantics +
      // remove-all edge). A removed NEW upload just drops — never attached.
      const entry = files.find((f) => f.uid === uid)
      if (entry?.persisted && entry.signed_id) {
        const sid = entry.signed_id
        setRemovedSignedIds((ids) => (ids.includes(sid) ? ids : [...ids, sid]))
      }

      setFiles((prev) => prev.filter((f) => f.uid !== uid))
    },
    [files],
  )

  // OKS-623 — restore focus after a removal commits (UX Spec §6). Runs
  // after React paints the new list so the target node exists; no async
  // timing. Consumes `pendingFocusRef` (set only by `handleRemove`) — other
  // `files` changes (uploads, mount) leave it null and steal no focus.
  useEffect(() => {
    const target = pendingFocusRef.current
    if (!target) return
    pendingFocusRef.current = null

    const rootEl = rootRef.current
    if (!rootEl) return

    const browse = rootEl.querySelector<HTMLElement>(
      '[data-testid="mission-attachments-browse"]',
    )
    if (target.type === "browse") {
      browse?.focus()
      return
    }

    // Match the target X by exact data-testid via a JS string comparison —
    // uids may embed a filename, which is unsafe inside a CSS attribute
    // selector (quotes/brackets would throw). Null-guard: rapid successive
    // removals may have dropped the computed target → fall back to browse.
    const node = Array.from(
      rootEl.querySelectorAll<HTMLElement>("[data-testid]"),
    ).find(
      (el) => el.dataset.testid === `mission-attachment-remove-${target.uid}`,
    )
    ;(node ?? browse)?.focus()
  }, [files])

  return (
    <div ref={rootRef} data-testid={testId} className="flex flex-col gap-3">
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setIsDragOver(true)
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        data-drag-over={isDragOver ? "true" : "false"}
        className={cn(
          "flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-center transition-colors",
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-input bg-background",
        )}
      >
        <Paperclip
          className="text-muted-foreground size-5"
          aria-hidden="true"
        />
        <p className="text-muted-foreground text-sm">
          {t("missions.form.attachments.drop_here")}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          data-testid="mission-attachments-browse"
        >
          {t("missions.form.attachments.label")}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_MIME}
          onChange={handleFileInput}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
          data-testid="mission-attachments-input"
        />
      </div>

      <p className="text-muted-foreground text-xs">
        {t("missions.form.attachments.helper")}
      </p>

      {/* OKS-928 — SERVER-side rejections for `mission[files][]`
          (`Mission#files_within_caps`: too_many / too_large /
          invalid_content_type). These were added by the model, counted in
          the form's error-summary alert, and rendered NOWHERE, so the user
          saw "3 erreurs" with only two of them locatable.

          One `<InputError>` per message, not one joined string: the server
          adds one error per rejected file (`errors.group_by_attribute`
          yields an array), and UX Spec §2 wants one line per file naming
          the file. `InputError` joins its `messages` with ", ", so passing
          the whole array would collapse every rejection onto a single
          line. Distinct from the per-file client-side `error` on a row
          below, which is pre-upload rejection — a different signal. */}
      {errors && errors.length > 0 && (
        <div
          className="flex flex-col gap-1"
          data-testid="mission-attachments-server-errors"
        >
          {errors.map((message, index) => (
            <InputError
              key={`${index}-${message}`}
              messages={[message]}
              // OKS-928 — `break-words` (`overflow-wrap: break-word`), NOT
              // `break-all`. These messages interpolate a raw `%{filename}`,
              // and a filename is a single unbroken token: normal prose wrap
              // happens at spaces, so a long name would overflow the ~343px
              // content box at 375px (UX Spec §5 requires it to wrap).
              // `break-words` breaks a token ONLY when it cannot otherwise
              // fit, leaving the surrounding sentence wrapping normally;
              // `break-all` breaks anywhere and would mangle the prose these
              // same paragraphs carry. Reach for `break-words` here.
              //
              // Applied at the call site rather than inside `InputError`,
              // which has 52 non-test callers — widening it is a separate
              // decision, tracked separately. The per-file rows below need
              // no equivalent: they already use `truncate` (`:409`).
              className="break-words"
              data-testid="mission-attachments-server-error"
            />
          ))}
        </div>
      )}

      <ul
        role="status"
        aria-live="polite"
        className="flex flex-col gap-2"
        data-testid="mission-attachments-list"
      >
        {files.map((file) => (
          <li
            key={file.uid}
            data-testid={`mission-attachment-${file.uid}`}
            data-status={file.status}
            className={cn(
              "flex items-center gap-2 rounded-md border px-3 py-2 text-sm",
              file.status === "error"
                ? "border-destructive bg-destructive/5"
                : "border-input bg-background",
            )}
          >
            {file.status === "uploading" && (
              <Loader2
                className="size-4 shrink-0 animate-spin"
                aria-hidden="true"
              />
            )}
            <span className="flex-1 truncate">{file.filename}</span>
            {file.status === "ready" && (
              <span className="text-muted-foreground text-xs">
                {Math.ceil(file.byte_size / 1024)} KB
              </span>
            )}
            {file.status === "error" && (
              <span className="text-destructive text-xs">{file.error}</span>
            )}
            {file.status !== "error" && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemove(file.uid)}
                aria-label={`${file.filename} — ${t("missions.form.attachments.remove")}`}
                data-testid={`mission-attachment-remove-${file.uid}`}
                // OKS-623 — visible box stays `size-7` (row height unchanged),
                // but an absolutely-positioned `::before` overlay expands the
                // interactive hit area to ≥44×44px (WCAG 2.5.5). The overlay
                // is centered on the glyph and does not affect layout.
                className="relative size-7 p-0 before:absolute before:top-1/2 before:left-1/2 before:min-h-11 before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']"
              >
                <X className="size-3.5" aria-hidden="true" />
              </Button>
            )}
            {/* Hidden wire for NEW uploads only (OKS-610): kept existing
                files are not re-submitted — the server preserves them. */}
            {file.status === "ready" && file.signed_id && !file.persisted && (
              <input type="hidden" name={name} value={file.signed_id} />
            )}
          </li>
        ))}
      </ul>

      {/* OKS-610 — removal signals for EXISTING files the user X'd. The
          server scans this mission's attachments, matches blob.signed_id,
          and purges the match (brand-scoped, idempotent). */}
      {removedSignedIds.map((sid) => (
        <input
          key={`remove-${sid}`}
          type="hidden"
          name={removeName}
          value={sid}
          data-testid="mission-attachment-remove-signal"
        />
      ))}
    </div>
  )
}

/**
 * Streams `file` to Rails Active Storage via DirectUpload, then promotes
 * the matching `FileEntry` (matched by `uid`) to `status: "ready"` with
 * the `signed_id` that the form will then ride. Errors flip the entry to
 * `status: "error"` with the network failure message.
 */
function kickOffUpload(
  file: File,
  uid: string,
  setFiles: React.Dispatch<React.SetStateAction<FileEntry[]>>,
) {
  const upload = new DirectUpload(file, DIRECT_UPLOAD_URL, {
    directUploadWillStoreFileWithXHR(xhr) {
      xhr.upload.addEventListener("progress", (event) => {
        if (!event.lengthComputable) return
        const progress = Math.round((event.loaded / event.total) * 100)
        setFiles((prev) =>
          prev.map((f) => (f.uid === uid ? { ...f, progress } : f)),
        )
      })
    },
  })

  upload.create((error, blob) => {
    if (error) {
      setFiles((prev) =>
        prev.map((f) =>
          f.uid === uid
            ? { ...f, status: "error", error: error.message, progress: 0 }
            : f,
        ),
      )
      return
    }
    setFiles((prev) =>
      prev.map((f) =>
        f.uid === uid
          ? { ...f, status: "ready", signed_id: blob.signed_id, progress: 100 }
          : f,
      ),
    )
  })
}
